import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiArrowUpRight,
  FiBriefcase,
  FiBarChart2,
  FiCalendar,
  FiCheck,
  FiClock,
  FiDatabase,
  FiFileText,
  FiGrid,
  FiLayers,
  FiMessageSquare,
  FiShield,
  FiX,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import "./Home.css";

const COPILOT_URL = "https://hadritech-rh-copilot.vercel.app";
const PORTAL_URL = "https://hadritech-rh-portal.vercel.app";

const copilotFeatures = [
  { icon: FiMessageSquare, title: "IA especializada", text: "Orientação prática para rotinas, tabelas, SQL e diagnósticos no Protheus RH." },
  { icon: FiBarChart2, title: "Construtor de Relatórios", text: "Crie relatórios personalizados a partir das estruturas e dados do Protheus, com uma experiência visual e guiada." },
  { icon: FiFileText, title: "Construtor de Documentos", text: "Monte documentos e modelos profissionais utilizando campos e informações do Protheus, com mais agilidade e padronização." },
];

const portalFeatureCatalog = [
  { id: "documents-payments", icon: FiFileText, title: "Documentos e pagamentos", text: "Holerites, informes e documentos do colaborador em um só lugar.", category: "Documentos", status: "preview_pending", demoType: "illustration", contentRef: null, order: 1 },
  { id: "time-attendance", icon: FiClock, title: "Ponto e jornada", text: "Consulta de marcações, espelho e solicitações relacionadas à jornada.", category: "Jornada", status: "preview_pending", demoType: "illustration", contentRef: null, order: 2 },
  { id: "vacations-requests", icon: FiCalendar, title: "Férias e solicitações", text: "Acompanhamento de pedidos, aprovações e histórico do colaborador.", category: "Solicitações", status: "preview_pending", demoType: "illustration", contentRef: null, order: 3 },
  { id: "company-management", icon: FiUsers, title: "Gestão para empresas", text: "Uma experiência pensada para pessoas, RH e gestores.", category: "Administração", status: "preview_pending", demoType: "illustration", contentRef: null, order: 4 },
];

function ExternalLink({ children, className, href, ...props }) {
  return (
    <a href={href} className={className} target="_blank" rel="noreferrer" {...props}>
      {children}
    </a>
  );
}

function FeaturePreviewModal({ feature, onClose }) {
  const closeButtonRef = React.useRef(null);
  const Icon = feature.icon;

  React.useEffect(() => {
    closeButtonRef.current?.focus();
    const handleEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  return (
    <div className="feature-preview-modal" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="feature-preview-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="feature-preview-title" onKeyDown={(event) => {
        if (event.key === "Tab") {
          event.preventDefault();
          closeButtonRef.current?.focus();
        }
      }}>
        <div className="feature-preview-modal__header">
          <div>
            <p className="ecosystem-kicker ecosystem-kicker--light">Prévia ilustrativa · Portal RH</p>
            <h2 id="feature-preview-title">{feature.title}</h2>
          </div>
          <button ref={closeButtonRef} type="button" className="feature-preview-modal__close" onClick={onClose} aria-label="Fechar demonstração">
            <FiX aria-hidden="true" />
          </button>
        </div>
        <div className="feature-preview-modal__visual" aria-label="Área reservada para a demonstração">
          <span><Icon aria-hidden="true" /></span>
          <strong>Demonstração em preparação</strong>
          <p>Em breve, você poderá conhecer esta funcionalidade do Portal RH.</p>
        </div>
        <p className="feature-preview-modal__description">{feature.text}</p>
        <p className="feature-preview-modal__note">Esta é uma prévia ilustrativa. A demonstração final será disponibilizada após a aprovação das telas reais.</p>
      </div>
    </div>
  );
}

export default function Home() {
  React.useEffect(() => {
    const elements = document.querySelectorAll(".ecosystem-home .ux-reveal");
    if (!("IntersectionObserver" in window)) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  const [selectedFeature, setSelectedFeature] = React.useState(null);
  const lastTriggerRef = React.useRef(null);

  const openFeaturePreview = (feature, event) => {
    lastTriggerRef.current = event.currentTarget;
    setSelectedFeature(feature);
  };

  const closeFeaturePreview = React.useCallback(() => {
    setSelectedFeature(null);
    window.setTimeout(() => lastTriggerRef.current?.focus(), 0);
  }, []);

  return (
    <div className="ecosystem-home">
      <section className="ecosystem-hero ux-reveal ux-reveal--hero" aria-labelledby="ecosystem-title">
        <div className="ecosystem-hero__grid" aria-hidden="true" />
        <div className="ecosystem-shell ecosystem-hero__content">
          <div className="ecosystem-hero__copy">
            <p className="ecosystem-eyebrow"><span /> Tecnologia Hadritech para o RH</p>
            <h1 id="ecosystem-title">
              Duas soluções.<br />
              <span>Uma experiência RH mais inteligente com <strong>TOTVS Protheus.</strong></span>
            </h1>
            <p className="ecosystem-hero__lead">
              Conheça o RH Copilot, que transforma a produtividade de quem trabalha com Protheus RH, e o Portal RH Hadritech, que conecta empresas, gestores e colaboradores em uma jornada digital completa.
            </p>
            <div className="ecosystem-hero__actions">
              <ExternalLink href={COPILOT_URL} className="ecosystem-button ecosystem-button--gold">
                Conhecer o RH Copilot <FiArrowRight aria-hidden="true" />
              </ExternalLink>
              <ExternalLink href={PORTAL_URL} className="ecosystem-button ecosystem-button--ghost">
                Conhecer o Portal RH
              </ExternalLink>
            </div>
            <div className="ecosystem-hero__trust" aria-label="Diferenciais do ecossistema">
              <span><FiShield aria-hidden="true" /> Ambientes independentes</span>
              <span><FiZap aria-hidden="true" /> Especialização em Protheus RH</span>
            </div>
          </div>

          <div className="ecosystem-switcher" aria-label="Soluções Hadritech">
            <ExternalLink href={COPILOT_URL} className="ecosystem-switcher__card ecosystem-switcher__card--active ux-reveal--child" aria-label="Conhecer o RH Copilot">
              <div className="ecosystem-switcher__topline">
                <span className="ecosystem-product-icon"><FiZap aria-hidden="true" /></span>
                <span className="ecosystem-status ecosystem-status--live">Disponível agora</span>
              </div>
              <p className="ecosystem-switcher__label">Para profissionais de RH e consultores</p>
              <h2>RH Copilot</h2>
              <p>IA, documentos e planilhas para acelerar o trabalho com Protheus RH.</p>
              <span className="ecosystem-switcher__link">Experimentar grátis <FiArrowUpRight aria-hidden="true" /></span>
            </ExternalLink>

            <ExternalLink href={PORTAL_URL} className="ecosystem-switcher__card ux-reveal--child" aria-label="Conhecer o Portal RH">
              <div className="ecosystem-switcher__topline">
                <span className="ecosystem-product-icon ecosystem-product-icon--soft"><FiBriefcase aria-hidden="true" /></span>
                <span className="ecosystem-status">Em desenvolvimento</span>
              </div>
              <p className="ecosystem-switcher__label">Para empresas, gestores e colaboradores</p>
              <h2>Portal RH</h2>
              <p>Uma nova experiência de autoatendimento e gestão de pessoas para sua empresa.</p>
              <span className="ecosystem-switcher__link ecosystem-switcher__link--muted">Conhecer o Portal RH <FiArrowUpRight aria-hidden="true" /></span>
            </ExternalLink>
          </div>
        </div>
      </section>

      <section className="ecosystem-intro ux-reveal" aria-label="Ecossistema Hadritech">
        <div className="ecosystem-shell ecosystem-intro__inner">
          <p>Um ponto de entrada para soluções que conectam</p>
          <div className="ecosystem-intro__items">
            <span><FiLayers aria-hidden="true" /> Tecnologia</span>
            <span><FiDatabase aria-hidden="true" /> Conhecimento</span>
            <span><FiUsers aria-hidden="true" /> Pessoas</span>
          </div>
        </div>
      </section>

      <section id="rh-copilot" className="ecosystem-section ecosystem-copilot ux-reveal" aria-labelledby="copilot-title">
        <div className="ecosystem-shell">
          <div className="ecosystem-section__heading ecosystem-section__heading--split">
            <div>
              <p className="ecosystem-kicker">01 · RH Copilot</p>
              <h2 id="copilot-title">Seu trabalho no Protheus RH,<br /><span>com mais contexto e velocidade.</span></h2>
            </div>
            <p>Uma plataforma especializada para transformar dúvidas, arquivos e demandas técnicas em próximos passos claros.</p>
          </div>

          <div className="ecosystem-copilot__layout">
            <div className="ecosystem-workspace" aria-label="Demonstração visual do RH Copilot">
              <div className="ecosystem-workspace__bar">
                <span><i /> Workspace Hadritech</span>
                <span>RH COPILOT</span>
              </div>
              <div className="ecosystem-workspace__body">
                <div className="ecosystem-workspace__rail" aria-hidden="true">
                  <span className="active"><FiZap /></span><span><FiMessageSquare /></span><span><FiFileText /></span><span><FiGrid /></span>
                </div>
                <div className="ecosystem-workspace__conversation">
                  <p className="ecosystem-workspace__caption">Assistente especializada · Protheus RH</p>
                  <div className="ecosystem-workspace__question">Preciso conferir uma rotina de férias e documentar o teste.</div>
                  <div className="ecosystem-workspace__answer">
                    <span className="ecosystem-workspace__avatar"><FiZap /></span>
                    <div>
                      <strong>Vamos organizar em três etapas:</strong>
                      <ol>
                        <li><span>1</span> Validar cadastros e parâmetros</li>
                        <li><span>2</span> Conferir cálculo e incidências</li>
                        <li><span>3</span> Gerar o roteiro de homologação</li>
                      </ol>
                    </div>
                  </div>
                  <p className="ecosystem-workspace__disclaimer">Demonstração visual · conteúdo ilustrativo</p>
                </div>
              </div>
            </div>

            <div className="ecosystem-feature-list">
              {copilotFeatures.map(({ icon: Icon, title, text }) => (
                <article key={title} className="ecosystem-feature-list__item ux-reveal--child">
                  <span><Icon aria-hidden="true" /></span>
                  <div><h3>{title}</h3><p>{text}</p></div>
                </article>
              ))}
              <div className="ecosystem-feature-list__actions">
                <ExternalLink href={COPILOT_URL} className="ecosystem-button ecosystem-button--navy">
                  Experimentar grátis <FiArrowUpRight aria-hidden="true" />
                </ExternalLink>
                <ExternalLink href={COPILOT_URL} className="ecosystem-text-link">Entrar</ExternalLink>
                <ExternalLink href={COPILOT_URL} className="ecosystem-text-link">Ver planos</ExternalLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="portal-rh" className="ecosystem-section ecosystem-portal ux-reveal" aria-labelledby="portal-title">
        <div className="ecosystem-shell ecosystem-portal__layout">
          <div className="ecosystem-portal__copy">
            <p className="ecosystem-kicker ecosystem-kicker--light">02 · Portal RH empresarial</p>
            <span className="ecosystem-portal__badge">Produto em desenvolvimento</span>
            <h2 id="portal-title">A experiência do colaborador,<br /><span>mais simples e próxima.</span></h2>
            <p className="ecosystem-portal__lead">O próximo produto do ecossistema Hadritech nasce para reunir serviços de RH em uma experiência clara, responsiva e preparada para a realidade de cada empresa.</p>
            <div className="ecosystem-portal__actions">
              <Link to="/contato?assunto=demonstracao-portal-rh" className="ecosystem-button ecosystem-button--gold">
                Solicitar demonstração <FiArrowRight aria-hidden="true" />
              </Link>
              <button type="button" className="ecosystem-button ecosystem-button--disabled" disabled title="O Portal RH ainda não possui uma URL pública validada">
                Acesso em breve
              </button>
            </div>
            <p className="ecosystem-portal__note"><FiCheck aria-hidden="true" /> O acesso será liberado aqui assim que o ambiente estiver disponível.</p>
          </div>

          <div className="ecosystem-portal__roadmap" aria-label="Escopo planejado para o Portal RH">
            {portalFeatureCatalog.map((feature) => {
              const Icon = feature.icon;
              return (
              <button key={feature.id} type="button" className="ecosystem-portal__roadmap-card" onClick={(event) => openFeaturePreview(feature, event)}>
                <div className="ecosystem-portal__roadmap-top"><span><Icon aria-hidden="true" /></span><small>0{feature.order}</small></div>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
                <span className="ecosystem-portal__roadmap-hint">Ver prévia <FiArrowUpRight aria-hidden="true" /></span>
              </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="ecosystem-section ecosystem-path" aria-labelledby="path-title">
        <div className="ecosystem-shell">
          <div className="ecosystem-section__heading ecosystem-section__heading--center">
            <p className="ecosystem-kicker">Escolha sua jornada</p>
            <h2 id="path-title">Por onde você quer começar?</h2>
            <p>Dois produtos independentes, conectados pela mesma especialização em tecnologia para RH.</p>
          </div>
          <div className="ecosystem-path__cards">
            <article>
              <span className="ecosystem-path__number">01</span>
              <div><p>Quero produtividade agora</p><h3>Trabalho com Protheus RH</h3><span>IA especializada, documentos, planilhas e relatórios.</span></div>
              <ExternalLink href={COPILOT_URL} aria-label="Ir para o RH Copilot"><FiArrowUpRight /></ExternalLink>
            </article>
            <article>
              <span className="ecosystem-path__number">02</span>
              <div><p>Quero transformar a experiência do time</p><h3>Represento uma empresa</h3><span>Portal de autoatendimento e gestão de pessoas.</span></div>
              <Link to="/contato?assunto=portal-rh" aria-label="Falar sobre o Portal RH"><FiArrowRight /></Link>
            </article>
          </div>
        </div>
      </section>

      <section className="ecosystem-final-cta" aria-labelledby="final-cta-title">
        <div className="ecosystem-shell ecosystem-final-cta__inner">
          <div><p className="ecosystem-kicker ecosystem-kicker--light">Ecossistema Hadritech</p><h2 id="final-cta-title">Tecnologia que entende o seu RH.</h2><p>Comece agora com o RH Copilot ou converse com a gente sobre o Portal RH.</p></div>
          <div>
            <ExternalLink href={COPILOT_URL} className="ecosystem-button ecosystem-button--gold">Experimentar o Copilot <FiArrowUpRight /></ExternalLink>
            <Link to="/contato" className="ecosystem-button ecosystem-button--ghost">Falar com a Hadritech</Link>
          </div>
        </div>
      </section>
      {selectedFeature && <FeaturePreviewModal feature={selectedFeature} onClose={closeFeaturePreview} />}
    </div>
  );
}
